import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { CSVLink } from "react-csv";
import { getApiBaseUrl, getDriverApiBaseUrl } from "../../utils/utils";

pdfMake.vfs = pdfFonts.vfs;

/* ─── SearchableSelect ───────────────────────────────────────────────── */
function SearchableSelect({ value, onChange, options, placeholder = "Search…", disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [query, options]);

  const selected = options.find((o) => String(o.value) === String(value));

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setQuery("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      {/* Trigger */}
      <div
        onClick={handleOpen}
        style={{
          height: 40,
          padding: "0 36px 0 12px",
          border: `1px solid ${open ? "#0f766e" : "#d1d5db"}`,
          borderRadius: 6,
          fontSize: 13,
          background: disabled ? "#f9fafb" : "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          color: selected ? "#111827" : "#9ca3af",
          userSelect: "none",
          boxShadow: open ? "0 0 0 2px #ccfbf1" : "none",
          transition: "border-color 0.15s, box-shadow 0.15s",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          position: "relative",
        }}
      >
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.label : placeholder}
        </span>
        <span style={{
          position: "absolute", right: 10,
          color: "#9ca3af", fontSize: 10, pointerEvents: "none",
        }}>
          {open ? "▲" : "▼"}
        </span>
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 4px)",
          left: 0, right: 0,
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          zIndex: 9999,
          overflow: "hidden",
        }}>
          {/* Search box */}
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
            <div style={{ position: "relative" }}>
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type to search…"
                style={{
                  width: "100%",
                  height: 34,
                  padding: "0 32px 0 10px",
                  border: "1px solid #e5e7eb",
                  borderRadius: 5,
                  fontSize: 13,
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  style={{
                    position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer",
                    color: "#9ca3af", fontSize: 14, lineHeight: 1, padding: 0,
                  }}
                >×</button>
              )}
            </div>
          </div>

          {/* Options list */}
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "12px 14px", fontSize: 13, color: "#9ca3af", textAlign: "center" }}>
                No results for "{query}"
              </div>
            ) : (
              filtered.map((o) => {
                const isActive = String(o.value) === String(value);
                return (
                  <div
                    key={o.value}
                    onMouseDown={() => handleSelect(o.value)}
                    style={{
                      padding: "9px 14px",
                      fontSize: 13,
                      cursor: "pointer",
                      background: isActive ? "#f0fdfa" : "transparent",
                      color: isActive ? "#0f766e" : "#111827",
                      fontWeight: isActive ? 700 : 400,
                      borderLeft: isActive ? "3px solid #0f766e" : "3px solid transparent",
                    }}
                  >
                    {o.label}
                  </div>
                );
              })
            )}
          </div>

          {filtered.length > 0 && (
            <div style={{ padding: "6px 12px", borderTop: "1px solid #f1f5f9", fontSize: 11, color: "#9ca3af" }}>
              {filtered.length} option{filtered.length !== 1 ? "s" : ""}
              {query ? ` matching "${query}"` : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const csvHeaders = [
  { label: "AWB", key: "awb" },
  { label: "Status", key: "status" },
  { label: "Order Date", key: "orderDate" },
  { label: "Last Transit Date", key: "lastTransitDate" },
  { label: "Last Transit Time", key: "lastTransitTime" },
  { label: "Partner", key: "partnerName" },
  { label: "Driver Name", key: "driverName" },
  { label: "Shipper", key: "shipper" },
  { label: "Consignee", key: "consignee" },
  { label: "Pickup Location", key: "pickupLocation" },
  { label: "Ambient Count", key: "ambientCount" },
  { label: "Ambient Temp", key: "ambientTemperature" },
  { label: "Chilled Count", key: "chilledCount" },
  { label: "Chilled Temp", key: "chilledTemperature" },
  { label: "Frozen Count", key: "frozenCount" },
  { label: "Frozen Temp", key: "frozenTemperature" },
  { label: "Total Samples", key: "totalSamples" },
  { label: "Handover By", key: "handoverName" },
  { label: "Received By", key: "receiverName" },
  { label: "Updated By", key: "updatedBy" },
];

const pdfFieldOptions = [
  { key: "awb", label: "AWB", width: "auto" },
  { key: "status", label: "Status", width: "auto" },
  { key: "orderDate", label: "Order Date", width: "auto" },
  { key: "lastTransitDate", label: "Transit Date", width: "auto" },
  { key: "lastTransitTime", label: "Transit Time", width: "auto" },
  { key: "partnerName", label: "Partner", width: "*" },
  { key: "driverName", label: "Driver Name", width: "*" },
  { key: "shipper", label: "Shipper", width: "*" },
  { key: "consignee", label: "Consignee", width: "*" },
  { key: "pickupLocation", label: "Pickup Location", width: "*" },
  { key: "ambientCount", label: "Ambient", width: "auto" },
  { key: "ambientTemperature", label: "Ambient Temp", width: "auto" },
  { key: "chilledCount", label: "Chilled", width: "auto" },
  { key: "chilledTemperature", label: "Chilled Temp", width: "auto" },
  { key: "frozenCount", label: "Frozen", width: "auto" },
  { key: "frozenTemperature", label: "Frozen Temp", width: "auto" },
  { key: "totalSamples", label: "Total Samples", width: "auto" },
  { key: "handoverName", label: "Handover By", width: "*" },
  { key: "receiverName", label: "Received By", width: "*" },
  { key: "updatedBy", label: "Updated By", width: "*" },
];

const defaultPdfFieldKeys = [
  "awb",
  "status",
  "orderDate",
  "partnerName",
  "driverName",
  "shipper",
  "consignee",
  "ambientCount",
  "chilledCount",
  "frozenCount",
  "totalSamples",
  "receiverName",
];

const statusColors = {
  DELIVERED: { bg: "#e8f5e9", color: "#2e7d32" },
  PICKED_UP: { bg: "#e3f2fd", color: "#1565c0" },
  PENDING: { bg: "#fff3e0", color: "#e65100" },
  IN_TRANSIT: { bg: "#e8eaf6", color: "#283593" },
  UNASSIGNED: { bg: "#f3f4f6", color: "#4b5563" },
};

const cardStyle = {
  background: "#fff",
  borderRadius: "8px",
  padding: "18px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  border: "1px solid #e5e7eb",
};

const inputStyle = {
  height: "40px",
  padding: "8px 12px",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontSize: "13px",
  width: "100%",
  background: "#fff",
};

const buttonStyle = {
  height: "40px",
  padding: "0 18px",
  border: "none",
  borderRadius: "6px",
  fontSize: "13px",
  fontWeight: "700",
  cursor: "pointer",
};

const clean = (value) => (value === null || value === undefined ? "" : String(value).trim());

const hasReportValue = (value) => {
  const text = clean(value);
  return text && text !== "--";
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const splitDateTime = (value) => {
  const [date = "", time = ""] = clean(value).split(" ");
  return { date, time };
};

const normalizeStatus = (value) => clean(value).toUpperCase();

const formatStatusLabel = (value) => clean(value).replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase()) || "Unknown";

const getPartnerName = (partner) => clean(partner?.pname || partner?.name || partner?.partnerName);

const getSampleCounts = (row) => {
  const ambient = toNumber(row.ambientCount);
  const chilled = toNumber(row.chilledCount);
  const frozen = toNumber(row.frozenCount);
  return { ambient, chilled, frozen, total: ambient + chilled + frozen };
};

const getMedicalJobGroupKey = (value) => clean(value).replace(/_\d+(?=_|$)/, "");

const buildSampleLookup = (rows) => rows.reduce((lookup, row) => {
  const key = getMedicalJobGroupKey(row.awb || row.jobId);
  if (key && !lookup[key]) {
    lookup[key] = row;
  }
  return lookup;
}, {});

const enrichReportRow = (row, sampleLookup) => {
  const sampleRow = sampleLookup[getMedicalJobGroupKey(row.awb || row.jobId)] || {};
  const sampleShipper = hasReportValue(sampleRow.shipper) ? sampleRow.shipper : "";
  const samplePickup = hasReportValue(sampleRow.pickupLocation) ? sampleRow.pickupLocation : "";
  const sampleDriver = hasReportValue(sampleRow.driverName) ? sampleRow.driverName : "";
  const sampleConsignee = hasReportValue(sampleRow.consignee) ? sampleRow.consignee : "";

  return {
    ...row,
    shipper: hasReportValue(row.shipper) ? row.shipper : sampleShipper || "--",
    consignee: hasReportValue(row.consignee) ? row.consignee : sampleConsignee || "--",
    pickupLocation: hasReportValue(row.pickupLocation) ? row.pickupLocation : samplePickup || "--",
    driverName: hasReportValue(row.driverName) ? row.driverName : sampleDriver || "--",
  };
};

const chunkArray = (items, chunkSize) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
};

const normalizeReportRow = (row, partnerMap) => {
  const partnerId = clean(row.partnerId);
  const status = normalizeStatus(row.STATUS || row.status);
  const modifiedDate = clean(row.modifiedDate || row.scanDateTime || row.createdDate);
  const { date: lastTransitDate, time: lastTransitTime } = splitDateTime(modifiedDate);
  const counts = getSampleCounts(row);

  return {
    ...row,
    awb: clean(row.jobId || row.awb || row.businessRefNo),
    status,
    partnerId,
    partnerName: partnerMap[partnerId] || clean(row.partnerName) || clean(row.shipperName || row.shipperContactName) || (partnerId ? `Partner ${partnerId}` : "--"),
    driverName: clean(row.driverName || row.driver_name) || "--",
    shipper: clean(
      row.shipperContactName || row.shipperName || row.shipper ||
      row.pkpName || row.pickup_contact_name || row.shipperContact
    ) || "--",
    consignee: (() => {
      const name = clean(row.contactName || row.receiverName || row.consignee || row.receiver_name || row.recvrName);
      const addr = clean(row.contactAddress || row.delvAdd || row.deliveryAddress || row.delivery_address || row.contactAdd);
      if (name && addr) return `${name} - ${addr}`;
      if (name) return name;
      if (addr) return addr;
      return "--";
    })(),
    pickupLocation: clean(
      row.pickupLocation || row.shipperFormattedAddress || row.pickupAddress ||
      row.pkpAdd || row.pickup_location || row.pickup_address ||
      row.shipperAddress || row.shipperAdd
    ) || "--",
    orderDate: clean(row.orderDate),
    modifiedDate,
    lastTransitDate,
    lastTransitTime,
    ambientCount: counts.ambient,
    chilledCount: counts.chilled,
    frozenCount: counts.frozen,
    totalSamples: counts.total,
    ambientTemperature: clean(row.ambientTemperature) || "--",
    chilledTemperature: clean(row.chilledTemperature) || "--",
    frozenTemperature: clean(row.frozenTemperature) || "--",
    handoverName: clean(row.handoverName || row.collectedBy) || "--",
    receiverName: clean(row.receiverName) || "--",
    updatedBy: clean(row.updatedBy || row.updated_by) || clean(row.driverName) || "--",
  };
};

const buildCsvRows = (rows) => rows.map((row) => ({
  awb: row.awb,
  status: row.status,
  orderDate: row.orderDate,
  lastTransitDate: row.lastTransitDate,
  lastTransitTime: row.lastTransitTime,
  partnerName: row.partnerName,
  driverName: row.driverName,
  shipper: row.shipper,
  consignee: row.consignee,
  pickupLocation: row.pickupLocation,
  ambientCount: row.ambientCount,
  ambientTemperature: row.ambientTemperature,
  chilledCount: row.chilledCount,
  chilledTemperature: row.chilledTemperature,
  frozenCount: row.frozenCount,
  frozenTemperature: row.frozenTemperature,
  totalSamples: row.totalSamples,
  handoverName: row.handoverName,
  receiverName: row.receiverName,
  updatedBy: row.updatedBy,
}));

/**
 * Let a long unbroken value wrap inside a narrow column.
 *
 * pdfmake will not break a run of non-space characters, so one value like
 * "UB38-26A6564_1_test" sets a hard floor under its column's width. With a dozen or more columns
 * those floors add up to more than the sheet and the right-hand columns are cut off the edge of
 * the paper with nothing on the page to say so. Inserting a ZERO-WIDTH SPACE at the existing
 * separators (and, failing those, every few characters) gives the layout somewhere legal to break:
 * the text is unchanged to the eye and still copies out clean, but the column can now be as narrow
 * as the page needs.
 */
const SOFT_BREAK = "\u200B";
const softWrapLongToken = (text) => {
  if (!text) return text;
  return text
    .split(" ")
    .map((token) => {
      if (token.length <= 12) return token;
      // Prefer breaking where the value already has a seam.
      let seamed = token.replace(/([_\-/.,:])/g, `$1${SOFT_BREAK}`);
      // Anything still longer than 12 unbroken characters gets a break every 8.
      seamed = seamed.replace(/[^\u200B]{8}(?=[^\u200B])/g, (chunk) => chunk + SOFT_BREAK);
      return seamed;
    })
    .join(" ");
};

const getPdfCellText = (row, key) => {
  const value = row[key];
  if (value === null || value === undefined || value === "") return "--";
  return softWrapLongToken(String(value));
};

const dedupeRows = (rows) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = row.orderId || row.awb || `${row.partnerId}-${row.driverId}-${row.orderDate}-${row.shipper}-${row.consignee}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export default function MedicalOrderGenerator() {
  const reportRequestIdRef = useRef(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [mode, setMode] = useState("partner");
  const [selectedPartner, setSelectedPartner] = useState("0");
  const [selectedDriver, setSelectedDriver] = useState("");
  const [partners, setPartners] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [shipmentDetails, setShipmentDetails] = useState([]);
  const [sampleCollections, setSampleCollections] = useState([]);
  // Statuses to include in the report. EMPTY MEANS ALL - there is no "ALL" sentinel to keep in
  // step with the real status values, and "nothing ticked" is the state the screen opens in, so
  // the natural reading (no filter applied) is also the correct one.
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const toggleStatus = (status) =>
    setSelectedStatuses((current) =>
      current.includes(status) ? current.filter((s) => s !== status) : [...current, status]);
  const [pdfMode, setPdfMode] = useState("classic");
  const [selectedPdfFields, setSelectedPdfFields] = useState(defaultPdfFieldKeys);
  const [loading, setLoading] = useState(false);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState("");
  const [sampleWarning, setSampleWarning] = useState("");
  const [lastRunLabel, setLastRunLabel] = useState("");

  const driverApiBaseUrl = getDriverApiBaseUrl();
  const crmApiBaseUrl = getApiBaseUrl();

  // ─── Move-Orders embed (reassign rows to a different partner) ───
  // Off by default. When enabled, each row gets a checkbox and a target-
  // partner action bar appears above the Track Summary table.
  // Hits /api/medical-order-mover/{target-partners,move}.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [moverTargets, setMoverTargets] = useState([]);
  const [moveToPartner, setMoveToPartner] = useState("");
  const [moveConfirmOpen, setMoveConfirmOpen] = useState(false);
  const [moving, setMoving] = useState(false);
  const [moveBanner, setMoveBanner] = useState(null);
  const [rules, setRules] = useState([]);            // [{id, field, value}]
  const [pendingField, setPendingField] = useState("partnerName");
  const [pendingValue, setPendingValue] = useState("");

  // Lazy-load target partner directory on first toggle of select mode.
  useEffect(() => {
    if (!selectMode || moverTargets.length > 0) return;
    (async () => {
      try {
        const r = await axios.get(`${driverApiBaseUrl}/shypvdriverapp/api/medical-order-mover/target-partners`);
        setMoverTargets(r.data?.partners || []);
      } catch (e) {
        setMoveBanner({ kind: "err", text: "Failed to load partner list: " + (e?.response?.data?.error || e.message) });
      }
    })();
  }, [selectMode, moverTargets.length, driverApiBaseUrl]);

  const toggleSelectOne = (orderId) => {
    if (orderId == null) return;
    setSelectedOrderIds((cur) => {
      const next = new Set(cur);
      next.has(orderId) ? next.delete(orderId) : next.add(orderId);
      return next;
    });
  };

  const targetPartnerName = () => {
    const p = moverTargets.find((x) => String(x.id) === String(moveToPartner));
    return p ? p.partner_name : `partner ${moveToPartner}`;
  };


  const doMoveOrders = async () => {
    setMoving(true); setMoveBanner(null);
    try {
      const r = await axios.post(`${driverApiBaseUrl}/shypvdriverapp/api/medical-order-mover/move`, {
        order_ids: Array.from(selectedOrderIds),
        to_partner_id: Number(moveToPartner),
      });
      const d = r.data || {};
      setMoveBanner({
        kind: "ok",
        text: `Moved ${d.updated || 0} of ${d.requested || 0} order(s) to ${targetPartnerName()}.`,
      });
      setMoveConfirmOpen(false);
      setSelectedOrderIds(new Set());
      // Reload the current report so reassigned rows reflect the new partner.
      handleGetReport();
    } catch (e) {
      setMoveBanner({ kind: "err", text: e?.response?.data?.error || e.message });
    } finally {
      setMoving(false);
    }
  };

  const partnerMap = useMemo(() => {
    const map = {};
    partners.forEach((partner) => {
      map[String(partner.pid)] = getPartnerName(partner);
    });
    return map;
  }, [partners]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [partnerResponse, driverResponse] = await Promise.all([
          axios.get(`${crmApiBaseUrl}/v1.0/partner/retrieve_list?page=0`),
          axios.get(`${driverApiBaseUrl}/shypvdriverapp/driver/getAllDrivers`),
        ]);

        const medicalPartners = (partnerResponse.data?.data || [])
          .filter((partner) => clean(partner.accountType).toLowerCase() === "medical")
          .sort((a, b) => getPartnerName(a).localeCompare(getPartnerName(b)));
        const driverList = (driverResponse.data?.data || [])
          .map((driver) => ({
            driverId: String(driver.driverId),
            driverName: clean(driver.name),
          }))
          .filter((driver) => driver.driverId && driver.driverName)
          .sort((a, b) => a.driverName.localeCompare(b.driverName));

        setPartners(medicalPartners);
        setDrivers(driverList);
      } catch (lookupError) {
        setError(lookupError.response?.data?.message || lookupError.message || "Failed to load report filters");
      }
    };

    loadLookups();
  }, [crmApiBaseUrl, driverApiBaseUrl]);

  const statusOptions = useMemo(() => {
    const counts = shipmentDetails.reduce((accumulator, row) => {
      const status = row.status || "UNKNOWN";
      accumulator[status] = (accumulator[status] || 0) + 1;
      return accumulator;
    }, {});

    return Object.entries(counts)
      .sort(([statusA], [statusB]) => formatStatusLabel(statusA).localeCompare(formatStatusLabel(statusB)))
      .map(([status, count]) => ({ status, count }));
  }, [shipmentDetails]);

  const sampleLookup = useMemo(() => buildSampleLookup(sampleCollections), [sampleCollections]);

  const enrichedShipmentDetails = useMemo(() => (
    shipmentDetails.map((row) => enrichReportRow(row, sampleLookup))
  ), [shipmentDetails, sampleLookup]);

  const filteredShipmentDetails = useMemo(() => enrichedShipmentDetails.filter((row) => (
    selectedStatuses.length === 0 || selectedStatuses.includes(row.status)
  )), [enrichedShipmentDetails, selectedStatuses]);

  const filteredSampleCollections = useMemo(() => sampleCollections.filter((row) => (
    selectedStatuses.length === 0 || selectedStatuses.includes(row.status)
  )), [sampleCollections, selectedStatuses]);

  /** What the status filter is doing, in words - used on screen and printed on the report. */
  const statusFilterLabel = useMemo(() => {
    if (selectedStatuses.length === 0) return "All Status";
    if (selectedStatuses.length === 1) return formatStatusLabel(selectedStatuses[0]);
    // Name them while the list is short enough to read; past that a count is more useful than a
    // sentence that runs off the edge of the page.
    if (selectedStatuses.length <= 3) return selectedStatuses.map(formatStatusLabel).join(", ");
    return `${selectedStatuses.length} statuses selected`;
  }, [selectedStatuses]);

  // Rule builder — group options for pending field dropdown
  const pendingFieldGroups = useMemo(() => {
    const counts = {};
    filteredShipmentDetails.forEach((row) => {
      const val = String(row[pendingField] || "—");
      counts[val] = (counts[val] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [filteredShipmentDetails, pendingField]);

  // Compute how many orders each rule matches
  const ruleMatchCount = (rule) =>
    filteredShipmentDetails.filter((row) => String(row[rule.field] || "—") === rule.value).length;

  // Union of all order IDs matched by any rule
  const ruleMatchedIds = useMemo(() => {
    if (!rules.length) return new Set();
    const ids = new Set();
    filteredShipmentDetails.forEach((row) => {
      if (row.orderId == null) return;
      if (rules.some((r) => String(row[r.field] || "—") === r.value)) ids.add(row.orderId);
    });
    return ids;
  }, [filteredShipmentDetails, rules]);

  // Keep selectedOrderIds in sync with rules
  useEffect(() => {
    if (selectMode) setSelectedOrderIds(ruleMatchedIds);
  }, [ruleMatchedIds, selectMode]);

  const addRule = () => {
    if (!pendingValue) return;
    const duplicate = rules.some((r) => r.field === pendingField && r.value === pendingValue);
    if (duplicate) return;
    setRules((prev) => [...prev, { id: Date.now(), field: pendingField, value: pendingValue }]);
    setPendingValue("");
  };

  const removeRule = (id) => setRules((prev) => prev.filter((r) => r.id !== id));

  const FIELD_LABELS = { partnerName: "Partner", status: "Status", driverName: "Driver", orderDate: "Date", consignee: "Consignee", shipper: "Shipper", pickupLocation: "Pickup", awb: "AWB" };

  const selectedPdfColumns = useMemo(() => pdfFieldOptions.filter((field) => selectedPdfFields.includes(field.key)), [selectedPdfFields]);

  // How the current field selection will actually print.
  //
  // The report is one A4 landscape sheet per run and the type shrinks as columns are added, so
  // "will it fit" is knowable here, before anyone generates a PDF and finds the right-hand columns
  // cut off the edge of the paper. The thresholds mirror the font ladder in generateNewPdf.
  const pdfFit = useMemo(() => {
    // +1 for the No column; +1 more when AWB is repeated as the row's anchor.
    const columns = 1 + (selectedPdfFields.includes("awb") ? 1 : 0) + selectedPdfColumns.length;
    if (columns <= 11) {
      return { columns, tone: "ok", text: `${selectedPdfColumns.length} fields — comfortable on one page.` };
    }
    if (columns <= 14) {
      return { columns, tone: "ok", text: `${selectedPdfColumns.length} fields — fits on one page in smaller type.` };
    }
    if (columns <= 17) {
      return { columns, tone: "warn", text: `${selectedPdfColumns.length} fields — very small print. Drop a few for a more readable sheet.` };
    }
    return { columns, tone: "warn", text: `${selectedPdfColumns.length} fields — too many for one page; long values may still be clipped. Deselect some.` };
  }, [selectedPdfColumns, selectedPdfFields]);

  const stats = useMemo(() => {
    const delivered = filteredShipmentDetails.filter((row) => row.status === "DELIVERED").length;
    const pickedUp = filteredShipmentDetails.filter((row) => row.status === "PICKED_UP").length;
    const pending = filteredShipmentDetails.filter((row) => !["DELIVERED", "PICKED_UP"].includes(row.status)).length;
    const totalSamples = filteredShipmentDetails.reduce((sum, row) => sum + row.totalSamples, 0);
    const collectionTasks = filteredSampleCollections.length;
    const collectedUnits = filteredSampleCollections.reduce((sum, row) => {
      const count = row.totalSamples;
      const collected = row.sampleCollectedAt || !["UNASSIGNED", "PENDING", ""].includes(row.status);
      return sum + (count > 0 ? count : collected ? 1 : 0);
    }, 0);

    return {
      total: filteredShipmentDetails.length,
      delivered,
      pickedUp,
      pending,
      totalSamples,
      collectionTasks,
      collectedUnits,
    };
  }, [filteredShipmentDetails, filteredSampleCollections]);

  const validateFilters = () => {
    if (!fromDate || !toDate) {
      setError("Please select both From Date and To Date");
      return false;
    }
    if (new Date(fromDate) > new Date(toDate)) {
      setError("From Date cannot be after To Date");
      return false;
    }
    if (mode === "driver" && !selectedDriver) {
      setError("Please select a driver");
      return false;
    }
    setError("");
    return true;
  };

  const handleGetReport = async () => {
    if (!validateFilters()) return;

    const requestId = reportRequestIdRef.current + 1;
    reportRequestIdRef.current = requestId;
    setLoading(true);
    setSampleLoading(false);
    setShipmentDetails([]);
    setSampleCollections([]);
    // Reset to "every status" for the new result set: the previous run's statuses may not
    // even exist in it, and a filter carried over silently would show an empty report.
    setSelectedStatuses([]);
    setSampleWarning("");

    try {
      const reportEndpoint = mode === "driver"
        ? `${driverApiBaseUrl}/shypvdriverapp/jobs/getMedicalReportByDriverWithDateRange`
        : `${driverApiBaseUrl}/shypvdriverapp/jobs/getMedicalReportWithPartnerIdWithDateRange`;
      const reportParams = mode === "driver"
        ? { driverId: selectedDriver, fromDate, toDate }
        : { partnerId: selectedPartner || "0", fromDate, toDate };

      const sampleParams = {
        fromDate,
        toDate,
        ...(mode === "driver" ? { driverId: selectedDriver } : {}),
        ...(mode === "partner" && selectedPartner !== "0" ? { partnerId: selectedPartner } : {}),
      };

      const reportResponse = await axios.get(reportEndpoint, { params: reportParams });

      if (reportRequestIdRef.current !== requestId) return;

      if (!reportResponse.data?.success) {
        throw new Error(reportResponse.data?.message || "Failed to load medical report");
      }

      const reportRows = dedupeRows((reportResponse.data?.data || [])
        .filter((row) => clean(row.shipmentType).toLowerCase() === "medical")
        .map((row) => normalizeReportRow(row, partnerMap)));

      setShipmentDetails(reportRows);

      const label = mode === "driver"
        ? drivers.find((driver) => driver.driverId === selectedDriver)?.driverName || `Driver ${selectedDriver}`
        : selectedPartner === "0" ? "All medical partners" : partnerMap[selectedPartner] || `Partner ${selectedPartner}`;
      setLastRunLabel(`${label} | ${fromDate} to ${toDate}`);

      setSampleLoading(true);
      axios.get(`${driverApiBaseUrl}/shypvdriverapp/jobs/getMedicalSampleCollectionReport`, { params: sampleParams })
        .then((sampleResponse) => {
          if (reportRequestIdRef.current !== requestId) return;
          if (!sampleResponse.data?.success) {
            throw new Error(sampleResponse.data?.message || "Failed to load sample collection report");
          }

          const sampleRows = dedupeRows((sampleResponse.data?.data || [])
            .filter((row) => clean(row.shipmentType).toLowerCase() === "medical")
            .map((row) => normalizeReportRow(row, partnerMap)));

          setSampleCollections(sampleRows);
          if (!reportRows.length && !sampleRows.length) {
            setError("No medical report data found for the selected filters");
          } else {
            setError("");
          }
        })
        .catch((sampleError) => {
          if (reportRequestIdRef.current !== requestId) return;
          setSampleWarning(sampleError.response?.data?.message || sampleError.message || "Sample collection rows could not be refreshed");
        })
        .finally(() => {
          if (reportRequestIdRef.current === requestId) {
            setSampleLoading(false);
          }
        });
    } catch (reportError) {
      if (reportRequestIdRef.current !== requestId) return;
      setShipmentDetails([]);
      setSampleCollections([]);
      setError(reportError.response?.data?.message || reportError.message || "An error occurred while fetching report data");
    } finally {
      if (reportRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  const togglePdfField = (fieldKey) => {
    setSelectedPdfFields((currentFields) => (
      currentFields.includes(fieldKey)
        ? currentFields.filter((key) => key !== fieldKey)
        : [...currentFields, fieldKey]
    ));
  };

  const openPdfDocument = (docDefinition) => {
    const pdfWindow = window.open("", "_blank");
    if (!pdfWindow) {
      setError("Popup blocked. Please allow popups for this site.");
      setPdfLoading(false);
      return;
    }

    pdfMake.createPdf(docDefinition).getBlob((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      pdfWindow.location.href = blobUrl;
      setPdfLoading(false);
    });
  };

  const generateClassicPdf = async () => {
    if (!filteredShipmentDetails.length) {
      setError("No report rows available for PDF export");
      return;
    }

    setPdfLoading(true);

    // Pick-up / drop-off times come from the order status history — the same source the other
    // medical reports use: bookingId -> { PICKED_UP: ts, DELIVERED: ts }.
    const jobStatusTimesMap = {};
    try {
      const awbs = Array.from(new Set(filteredShipmentDetails.map((r) => r.awb).filter(Boolean)));
      if (awbs.length) {
        const res = await axios.get(`${crmApiBaseUrl}/shypv/order/task/status/Allhistory`, {
          params: { bookingIds: awbs.join(",") },
        });
        (res.data?.data || []).forEach((entry) => {
          const awb = entry.bookingId;
          const status = entry.typ;
          const time = entry.dt || null;
          if (!awb) return;
          if (!jobStatusTimesMap[awb]) jobStatusTimesMap[awb] = {};
          if (status && time) jobStatusTimesMap[awb][status] = time;
        });
      }
    } catch (e) {
      console.error("Classic PDF: failed to load status history (pickup/drop times)", e);
    }

    const grouped = {};
    filteredShipmentDetails.forEach((row) => {
      const key = `${row.orderDate || row.lastTransitDate || "No Date"}||${row.driverName || "--"}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    });

    const content = [
      { text: "TIME EXPRESS PARCELS DELIVERY LLC", style: "header", alignment: "center", margin: [0, 0, 0, 5] },
      { text: `Medical Report - ${lastRunLabel || `${fromDate} to ${toDate}`}`, style: "subheader", alignment: "center", margin: [0, 0, 0, 8] },
      { text: `Orders: ${stats.total} | Samples: ${stats.totalSamples} | Collection Tasks: ${stats.collectionTasks}`, alignment: "center", fontSize: 9, margin: [0, 0, 0, 12] },
    ];

    Object.entries(grouped).forEach(([key, rows]) => {
      const [date, driverName] = key.split("||");
      let ambientTotal = 0;
      let chilledTotal = 0;
      let frozenTotal = 0;

      const tableBody = [
        [
          { text: "No", rowSpan: 2, style: "tableHeader" },
          { text: "AWB", rowSpan: 2, style: "tableHeader" },
          { text: "Partner", rowSpan: 2, style: "tableHeader" },
          { text: "Driver Name", rowSpan: 2, style: "tableHeader" },
          { text: "Shipper", rowSpan: 2, style: "tableHeader" },
          { text: "Consignee", rowSpan: 2, style: "tableHeader" },
          { text: "Status", rowSpan: 2, style: "tableHeader" },
          { text: "Pickup Time", rowSpan: 2, style: "tableHeader" },
          { text: "Drop Time", rowSpan: 2, style: "tableHeader" },
          { text: "Samples (count / temp)", colSpan: 4, style: "tableHeader", alignment: "center" },
          {},
          {},
          {},
        ],
        [
          // Nine blanks: one per rowSpan:2 column above (No..Drop Time). This dropped from ten
          // when the Modified column was removed - one stray blank here and every sub-header
          // slides a column out of place.
          {},
          {},
          {},
          {},
          {},
          {},
          {},
          {},
          {},
          { text: "Ambient", style: "tableSubHeader" },
          { text: "Chilled", style: "tableSubHeader" },
          { text: "Frozen", style: "tableSubHeader" },
          { text: "Total", style: "tableSubHeader" },
        ],
      ];

      // count on top, medical temperature underneath (matches the driver medical report)
      const sampleCell = (count, temp) => ({
        stack: [
          { text: String(count ?? 0), alignment: "center" },
          { text: `temp - ${temp && temp !== "--" ? temp : "--"}`, alignment: "center", fontSize: 6, color: "#555555" },
        ],
      });

      rows.forEach((row, index) => {
        ambientTotal += row.ambientCount;
        chilledTotal += row.chilledCount;
        frozenTotal += row.frozenCount;
        const times = jobStatusTimesMap[row.awb] || {};
        tableBody.push([
          index + 1,
          row.awb || "--",
          row.partnerName || "--",
          row.driverName || "--",
          row.shipper || "--",
          row.consignee || "--",
          row.status || "--",
          times.PICKED_UP || "--",
          times.DELIVERED || "--",
          sampleCell(row.ambientCount, row.ambientTemperature),
          sampleCell(row.chilledCount, row.chilledTemperature),
          sampleCell(row.frozenCount, row.frozenTemperature),
          row.totalSamples,
        ]);
      });

      tableBody.push([
        // colSpan 9: the label runs across everything up to Drop Time. It was 10 before the
        // Modified column was removed - a stale span here silently shifts every sample total
        // one cell to the left.
        { text: "Total", colSpan: 9, alignment: "right", bold: true },
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        {},
        { text: ambientTotal, alignment: "center", bold: true },
        { text: chilledTotal, alignment: "center", bold: true },
        { text: frozenTotal, alignment: "center", bold: true },
        { text: ambientTotal + chilledTotal + frozenTotal, alignment: "center", bold: true },
      ]);

      content.push(
        { text: `${date} - ${driverName}`, style: "subheader", alignment: "center", margin: [0, 0, 0, 6] },
        // Centred by sitting the fixed-width table between two flexible spacers. The table is
        // narrower than the page, and pdfmake pins a fixed-width table to the left margin, which
        // left the report hanging off one side of the paper.
        {
          columns: [
            { width: "*", text: "" },
            {
              width: "auto",
              table: {
                headerRows: 2,
                // One width per column; the 52pt Modified column is gone.
                widths: [16, 50, 54, 54, 50, 68, 42, 48, 48, 34, 34, 34, 28],
                body: tableBody,
                // Keep one AWB's row whole: without this pdfmake splits a tall row across the
                // page break, so a single shipment's details arrive on two sheets of paper.
                dontBreakRows: true,
                // Never strand the two header rows at the foot of a page.
                keepWithHeaderRows: 2,
              },
              layout: "lightHorizontalLines",
              fontSize: 7,
            },
            { width: "*", text: "" },
          ],
          margin: [0, 0, 0, 14],
        }
      );
    });

    const docDefinition = {
      content,
      pageSize: "A4",
      pageOrientation: "landscape",
      styles: {
        header: { fontSize: 14, bold: true },
        subheader: { fontSize: 10, bold: true },
        tableHeader: { bold: true, fillColor: "#eeeeee", alignment: "center" },
        tableSubHeader: { bold: true, alignment: "center" },
      },
    };

    openPdfDocument(docDefinition);
  };

  const generateNewPdf = () => {
    if (!filteredShipmentDetails.length) {
      setError("No report rows available for PDF export");
      return;
    }
    if (!selectedPdfColumns.length) {
      setError("Select at least one field for the new PDF report");
      return;
    }

    setPdfLoading(true);
    const ambientTotal = filteredShipmentDetails.reduce((sum, row) => sum + row.ambientCount, 0);
    const chilledTotal = filteredShipmentDetails.reduce((sum, row) => sum + row.chilledCount, 0);
    const frozenTotal = filteredShipmentDetails.reduce((sum, row) => sum + row.frozenCount, 0);
    const driverSummary = filteredShipmentDetails.reduce((accumulator, row) => {
      const driverName = row.driverName || "--";
      if (!accumulator[driverName]) {
        accumulator[driverName] = { orders: 0, samples: 0 };
      }
      accumulator[driverName].orders += 1;
      accumulator[driverName].samples += row.totalSamples;
      return accumulator;
    }, {});

    // ONE section holding every selected field.
    //
    // This used to be chunkArray(selectedPdfColumns, 5): the fields were cut into groups of five
    // and each group was emitted as its own section carrying pageBreak: "before". Selecting more
    // than five fields therefore produced a report where ONE AWB's details were split across two
    // sheets of paper - "Fields 1-5" on one page and "Fields 6-10" on the next - which is the
    // opposite of what this report is for. The page is A4 landscape with "*" widths, so the
    // columns share the full sheet and one shipment stays one row on one page.
    //
    // The two branches downstream fall out correctly on their own: pdfColumnSections.length > 1
    // is now false (so the title stays plain "Report Details" with no "Fields x-y" suffix) and
    // sectionIndex > 0 is never true (so no pageBreak is attached).
    const pdfColumnSections = [selectedPdfColumns];
    const awbField = pdfFieldOptions.find((field) => field.key === "awb");

    // Fit the type to the number of columns.
    //
    // pdfmake gives each "*" column an equal share of the page, but it will not break a long
    // unbroken token - an AWB like "UB38-26A6564_1_test" or a status like "PICKUP_ENROUTE" sets a
    // floor under its column. Past a dozen or so columns those floors add up to more than the
    // sheet and the right-hand columns are simply cut off the edge of the paper. Smaller type
    // lowers every floor proportionally, so the whole report stays on the page instead of being
    // silently truncated - a cut-off column is worse than small print, because nothing on the page
    // says it is missing.
    const printedColumnCount =
      1 + (selectedPdfFields.includes("awb") ? 1 : 0) + selectedPdfColumns.length;
    const tableFontSize =
      printedColumnCount <= 8 ? 7.5
        : printedColumnCount <= 11 ? 6.5
        : printedColumnCount <= 14 ? 5.5
        : printedColumnCount <= 17 ? 5
        : 4.5;
    const tableHeaderFontSize = tableFontSize + 0.5;
    const detailSections = pdfColumnSections.flatMap((columns, sectionIndex) => {
      const shouldRepeatAwb = selectedPdfFields.includes("awb") && !columns.some((field) => field.key === "awb") && awbField;
      const tableColumns = [
        { key: "pdfRowNumber", label: "No" },
        ...(shouldRepeatAwb ? [awbField] : []),
        ...columns,
      ];
      const startField = sectionIndex * 5 + 1;
      const endField = startField + columns.length - 1;
      const sectionTitle = pdfColumnSections.length > 1
        ? `Report Details - Fields ${startField}-${endField}`
        : "Report Details";

      return [
        {
          text: sectionTitle,
          style: "sectionTitle",
          margin: sectionIndex === 0 ? [0, 2, 0, 5] : [0, 0, 0, 5],
          ...(sectionIndex > 0 ? { pageBreak: "before" } : {}),
        },
        {
          table: {
            headerRows: 1,
            widths: tableColumns.map((field) => (field.key === "pdfRowNumber" ? 16 : "*")),
            body: [
              tableColumns.map((field) => ({ text: field.label, style: "newTableHeader" })),
              ...filteredShipmentDetails.map((row, rowIndex) => tableColumns.map((field) => ({
                text: field.key === "pdfRowNumber" ? rowIndex + 1 : getPdfCellText(row, field.key),
                style: field.key === "pdfRowNumber" ? "rowNumberCell" : "newTableCell",
              }))),
            ],
          },
          layout: {
            fillColor: (rowIndex) => (rowIndex === 0 ? "#0f766e" : rowIndex % 2 === 0 ? "#f8fafc" : null),
            hLineColor: () => "#e2e8f0",
            vLineColor: () => "#e2e8f0",
          },
          dontBreakRows: true,
          fontSize: pdfColumnSections.length > 1 ? 6 : 7,
          margin: [0, 0, 0, 8],
        },
      ];
    });

    const docDefinition = {
      content: [
        { text: "TIME EXPRESS PARCELS DELIVERY LLC", style: "newHeader", alignment: "center" },
        { text: "Medical New PDF Report", style: "newTitle", alignment: "center", margin: [0, 2, 0, 8] },
        {
          columns: [
            { text: `Report: ${lastRunLabel || `${fromDate} to ${toDate}`}`, style: "summaryLine" },
            { text: `Status: ${statusFilterLabel}`, style: "summaryLine", alignment: "right" },
          ],
          margin: [0, 0, 0, 8],
        },
        {
          table: {
            widths: ["*", "*", "*", "*", "*", "*"],
            body: [
              [
                { text: "Orders", style: "metricLabel" },
                { text: "Delivered", style: "metricLabel" },
                { text: "Picked Up", style: "metricLabel" },
                { text: "Pending", style: "metricLabel" },
                { text: "Samples", style: "metricLabel" },
                { text: "Collected", style: "metricLabel" },
              ],
              [
                { text: stats.total, style: "metricValue" },
                { text: stats.delivered, style: "metricValue" },
                { text: stats.pickedUp, style: "metricValue" },
                { text: stats.pending, style: "metricValue" },
                { text: stats.totalSamples, style: "metricValue" },
                { text: stats.collectedUnits, style: "metricValue" },
              ],
            ],
          },
          layout: {
            fillColor: (rowIndex) => (rowIndex === 0 ? "#f1f5f9" : null),
            hLineColor: () => "#dbe4ee",
            vLineColor: () => "#dbe4ee",
          },
          margin: [0, 0, 0, 10],
        },
        {
          columns: [
            { text: `Ambient: ${ambientTotal}`, style: "summaryLine" },
            { text: `Chilled: ${chilledTotal}`, style: "summaryLine", alignment: "center" },
            { text: `Frozen: ${frozenTotal}`, style: "summaryLine", alignment: "right" },
          ],
          margin: [0, 0, 0, 8],
        },
        ...detailSections,
        {
          text: Object.entries(driverSummary)
            .map(([driverName, summary]) => `${driverName}: ${summary.orders} order(s), ${summary.samples} sample(s)`)
            .join("  |  "),
          style: "footerSummary",
          margin: [0, 10, 0, 0],
        },
      ],
      pageSize: "A4",
      pageOrientation: "landscape",
      styles: {
        newHeader: { fontSize: 13, bold: true, color: "#0f172a" },
        newTitle: { fontSize: 11, bold: true, color: "#0f766e" },
        summaryLine: { fontSize: 8, color: "#334155", bold: true },
        metricLabel: { fontSize: 7, color: "#475569", bold: true, alignment: "center" },
        metricValue: { fontSize: 12, color: "#111827", bold: true, alignment: "center" },
        sectionTitle: { fontSize: 8, bold: true, color: "#0f172a" },
        newTableHeader: { bold: true, color: "#ffffff", fontSize: tableHeaderFontSize, alignment: "center" },
        newTableCell: { fontSize: tableFontSize, color: "#111827" },
        rowNumberCell: { fontSize: tableFontSize, color: "#475569", alignment: "center" },
        footerSummary: { fontSize: 7, color: "#475569", italics: true },
      },
      // Tight margins: every point saved here is a point the table can use, and this report is
      // read on screen or filed, not bound.
      pageMargins: [12, 16, 12, 18],
      defaultStyle: {
        // Was `pdfColumnSections.length > 1 ? 6 : 7`, which keyed off the old column chunking and
        // therefore always evaluated to 7 once that was removed - the largest setting, on the
        // widest reports. It follows the column count now.
        fontSize: tableFontSize,
      },
    };

    openPdfDocument(docDefinition);
  };

  const generatePdf = () => {
    if (pdfMode === "classic") {
      generateClassicPdf().catch((e) => {
        console.error("Classic PDF generation failed", e);
        setError("Failed to generate the classic PDF");
        setPdfLoading(false);
      });
      return;
    }
    generateNewPdf();
  };

  const csvRows = buildCsvRows(filteredShipmentDetails);
  const hasRows = shipmentDetails.length > 0;

  /* ── Option lists for SearchableSelect ── */
  const partnerOptions = useMemo(() => [
    { value: "0", label: "All medical partners" },
    ...partners.map((p) => ({ value: String(p.pid), label: getPartnerName(p) })),
  ], [partners]);

  const driverOptions = useMemo(() => [
    { value: "", label: "Select driver" },
    ...drivers.map((d) => ({ value: d.driverId, label: `${d.driverId} — ${d.driverName}` })),
  ], [drivers]);

  const modeOptions = [
    { value: "partner", label: "By Partner" },
    { value: "driver",  label: "By Driver"  },
  ];

  const ruleFieldOptions = [
    { value: "partnerName",    label: "Partner"          },
    { value: "status",         label: "Status"           },
    { value: "driverName",     label: "Driver"           },
    { value: "orderDate",      label: "Date"             },
    { value: "consignee",      label: "Consignee"        },
    { value: "shipper",        label: "Shipper"          },
    { value: "pickupLocation", label: "Pickup Location"  },
    { value: "awb",            label: "AWB"              },
  ];

  const ruleValueOptions = useMemo(() => [
    { value: "", label: "— select value —" },
    ...pendingFieldGroups.map(([val, count]) => ({ value: val, label: `${val}  (${count})` })),
  ], [pendingFieldGroups]);

  const moverTargetOptions = useMemo(() => [
    { value: "", label: "— Pick target partner —" },
    ...moverTargets.map((p) => ({ value: String(p.id), label: `${p.partner_name}  ·  #${p.id}` })),
  ], [moverTargets]);

  return (
    <div style={{ padding: "24px", fontFamily: "'Segoe UI', sans-serif", background: "#f4f6f8", minHeight: "100vh" }}>

      {/* ── Page header ── */}
      <div style={{ marginBottom: "20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "700", color: "#111827", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              width: 36, height: 36, borderRadius: 8,
              background: "linear-gradient(135deg, #0f766e, #0d9488)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="fa fa-medkit" style={{ color: "#fff", fontSize: 16 }} />
            </span>
            Medical Reports
          </h2>
          {lastRunLabel && (
            <div style={{ marginTop: "6px", fontSize: "12px", color: "#64748b", paddingLeft: 46 }}>
              {lastRunLabel}
            </div>
          )}
        </div>
      </div>

      {/* ── Filters card ── */}
      <div style={{ ...cardStyle, marginBottom: "18px" }}>
        <div style={{ marginBottom: 14, fontSize: 12, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.8px" }}>
          Report Filters
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "160px 160px 180px 1fr auto", gap: "14px", alignItems: "end" }}>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>From Date</label>
            <input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>To Date</label>
            <input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>Report Mode</label>
            <SearchableSelect
              value={mode}
              onChange={setMode}
              options={modeOptions}
              placeholder="Select mode…"
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>
              {mode === "driver" ? "Driver" : "Partner"}
              {mode === "partner" && partners.length > 0 && (
                <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 6 }}>({partners.length} available)</span>
              )}
              {mode === "driver" && drivers.length > 0 && (
                <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 6 }}>({drivers.length} available)</span>
              )}
            </label>
            {mode === "driver" ? (
              <SearchableSelect
                value={selectedDriver}
                onChange={setSelectedDriver}
                options={driverOptions}
                placeholder="Search drivers…"
                disabled={drivers.length === 0}
              />
            ) : (
              <SearchableSelect
                value={selectedPartner}
                onChange={setSelectedPartner}
                options={partnerOptions}
                placeholder="Search partners…"
                disabled={partners.length === 0}
              />
            )}
          </div>
          <button
            type="button"
            onClick={handleGetReport}
            disabled={loading}
            style={{
              ...buttonStyle,
              background: loading ? "#94a3b8" : "#0f766e",
              color: "#fff",
              minWidth: 110,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 7,
            }}
          >
            {loading
              ? <><span style={{ display: "inline-block", width: 13, height: 13, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Loading</>
              : <><i className="fa fa-search" />  Get Report</>
            }
          </button>
        </div>
        {error && (
          <div style={{ marginTop: "12px", display: "flex", alignItems: "center", gap: 8, color: "#b91c1c", fontSize: "13px", fontWeight: "600", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "9px 12px" }}>
            <i className="fa fa-exclamation-circle" /> {error}
          </div>
        )}
        {sampleWarning && hasRows && (
          <div style={{ marginTop: "10px", display: "flex", alignItems: "center", gap: 8, color: "#92400e", fontSize: "13px", fontWeight: "600", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "9px 12px" }}>
            <i className="fa fa-warning" /> {sampleWarning}
          </div>
        )}
      </div>

      {(loading || sampleLoading || hasRows || sampleCollections.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(130px, 1fr))", gap: "12px", marginBottom: "18px" }}>
          {[
            ["Orders", stats.total, "#111827"],
            ["Delivered", stats.delivered, "#2e7d32"],
            ["Picked Up", stats.pickedUp, "#1565c0"],
            ["Pending", stats.pending, "#e65100"],
            ["Total Samples", stats.totalSamples, "#7b1fa2"],
            ["Samples Collected", stats.collectedUnits, "#0f766e"],
          ].map(([label, value, color]) => (
            <div key={label} style={{ ...cardStyle, padding: "14px 16px", borderLeft: `4px solid ${color}` }}>
              <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", fontWeight: "700" }}>{label}</div>
              <div style={{ fontSize: "25px", fontWeight: "800", color, marginTop: "4px" }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {hasRows && (
        <>
          <div style={{ ...cardStyle, marginBottom: "12px", padding: "14px 16px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 1fr) minmax(220px, 1fr) auto", gap: "12px", alignItems: "end" }}>
              <div>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "8px", marginBottom: "6px", flexWrap: "wrap" }}>
                  <label style={{ fontSize: "12px", fontWeight: "700", color: "#475569" }}>
                    Status <span style={{ fontWeight: 600, color: "#0f766e" }}>— {statusFilterLabel}</span>
                  </label>
                  {selectedStatuses.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedStatuses([])}
                      style={{ background: "none", border: "none", padding: 0, color: "#b91c1c", fontSize: "11px", fontWeight: 700, cursor: "pointer" }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {/* Tick any number of statuses; nothing ticked means every status. Counts come
                    from the unfiltered rows, so they do not move as the selection changes. */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", maxHeight: "104px", overflowY: "auto", padding: "7px", border: "1px solid #d1d5db", borderRadius: "6px", background: "#fff" }}>
                  {statusOptions.length === 0 && (
                    <span style={{ fontSize: "12px", color: "#94a3b8" }}>No statuses in this result</span>
                  )}
                  {statusOptions.map((option) => {
                    const checked = selectedStatuses.includes(option.status);
                    return (
                      <label
                        key={option.status}
                        style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "4px 9px", border: `1px solid ${checked ? "#0f766e" : "#e2e8f0"}`, borderRadius: "999px", background: checked ? "#f0fdfa" : "#fff", color: checked ? "#0f766e" : "#475569", fontSize: "11.5px", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleStatus(option.status)}
                          style={{ accentColor: "#0f766e", margin: 0 }}
                        />
                        <span>{formatStatusLabel(option.status)} ({option.count})</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px" }}>PDF Type</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", border: "1px solid #d1d5db", borderRadius: "6px", overflow: "hidden", height: "40px" }}>
                  {[
                    ["classic", "Classic PDF"],
                    ["new", "New PDF Report"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setPdfMode(value)}
                      style={{
                        border: "none",
                        background: pdfMode === value ? "#0f766e" : "#fff",
                        color: pdfMode === value ? "#fff" : "#334155",
                        fontSize: "12px",
                        fontWeight: "800",
                        cursor: "pointer",
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <CSVLink data={csvRows} headers={csvHeaders} filename="medical_report.csv" style={{ ...buttonStyle, display: "inline-flex", alignItems: "center", background: "#fff", color: "#0f766e", border: "1px solid #0f766e", textDecoration: "none" }}>
                <i className="fa fa-download" style={{ marginRight: "6px" }} />CSV
              </CSVLink>
              <button type="button" onClick={generatePdf} disabled={pdfLoading} style={{ ...buttonStyle, background: "#fff", color: "#2563eb", border: "1px solid #2563eb" }}>
                <i className="fa fa-file-pdf-o" style={{ marginRight: "6px" }} />{pdfLoading ? "Generating..." : pdfMode === "classic" ? "Classic PDF" : "New PDF Report"}
              </button>
            </div>
            </div>

            {pdfMode === "new" && (
              <div style={{ marginTop: "14px", borderTop: "1px solid #e5e7eb", paddingTop: "14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", marginBottom: "10px", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "10px", flexWrap: "wrap" }}>
                    <div style={{ fontSize: "12px", fontWeight: "800", color: "#334155", textTransform: "uppercase" }}>PDF Fields</div>
                    <span style={{ fontSize: "11px", fontWeight: 600, color: pdfFit.tone === "warn" ? "#b45309" : "#0f766e" }}>
                      {pdfFit.text}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button type="button" onClick={() => setSelectedPdfFields(pdfFieldOptions.map((field) => field.key))} style={{ ...buttonStyle, height: "32px", padding: "0 12px", background: "#eef6f5", color: "#0f766e", border: "1px solid #99d3ca" }}>Select All</button>
                    <button type="button" onClick={() => setSelectedPdfFields(defaultPdfFieldKeys)} style={{ ...buttonStyle, height: "32px", padding: "0 12px", background: "#fff", color: "#334155", border: "1px solid #cbd5e1" }}>Default</button>
                    <button type="button" onClick={() => setSelectedPdfFields([])} style={{ ...buttonStyle, height: "32px", padding: "0 12px", background: "#fff", color: "#b91c1c", border: "1px solid #fecaca" }}>Clear</button>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: "8px" }}>
                  {pdfFieldOptions.map((field) => {
                    const checked = selectedPdfFields.includes(field.key);
                    return (
                      <label key={field.key} style={{ display: "flex", alignItems: "center", gap: "8px", minHeight: "34px", padding: "7px 9px", border: `1px solid ${checked ? "#0f766e" : "#e2e8f0"}`, borderRadius: "6px", background: checked ? "#f0fdfa" : "#fff", color: "#334155", fontSize: "12px", fontWeight: "700", cursor: "pointer" }}>
                        <input type="checkbox" checked={checked} onChange={() => togglePdfField(field.key)} style={{ accentColor: "#0f766e" }} />
                        <span>{field.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <h4 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: "#111827" }}>Track Summary</h4>
              <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "700" }}>{filteredShipmentDetails.length} row(s)</span>
            </div>

            {/* ── Move-Orders toolbar (embedded mover) ── */}
            <div style={{
              padding: "10px 18px", borderBottom: "1px solid #e5e7eb",
              display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10,
              background: selectMode ? "#fef3c7" : "#f8fafc",
            }}>
              <button
                type="button"
                onClick={() => { setSelectMode(!selectMode); setSelectedOrderIds(new Set()); setMoveBanner(null); setRules([]); setPendingValue(""); }}
                style={{
                  padding: "6px 14px", border: "none", borderRadius: 6,
                  background: selectMode ? "#dc2626" : "#7c3aed", color: "#fff",
                  fontSize: 13, fontWeight: 600, cursor: "pointer",
                }}>
                <i className={`fa ${selectMode ? "fa-times" : "fa-exchange"}`} style={{ marginRight: 6 }} />
                {selectMode ? "Cancel move" : "Move Orders to Different Partner"}
              </button>
              {selectMode && (
                <span style={{ fontSize: 12, color: "#92400e", fontWeight: 600 }}>
                  Build rules below — matched orders highlight in the table
                </span>
              )}
              {!selectMode && moveBanner && (
                <span style={{ marginLeft: "auto", padding: "4px 10px", borderRadius: 14, fontSize: 12, fontWeight: 600, background: moveBanner.kind === "ok" ? "#dcfce7" : "#fee2e2", color: moveBanner.kind === "ok" ? "#166534" : "#991b1b" }}>
                  {moveBanner.text}
                </span>
              )}
            </div>
            {/* ── Rule Builder Panel ── */}
            {selectMode && (
              <div style={{ margin: "0", padding: "16px 18px", borderBottom: "1px solid #e5e7eb", background: "#fffbeb" }}>
                {/* Add Rule Row */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#92400e", whiteSpace: "nowrap" }}>+ Add Rule:</span>
                  <div style={{ minWidth: 160 }}>
                    <SearchableSelect
                      value={pendingField}
                      onChange={(v) => { setPendingField(v); setPendingValue(""); }}
                      options={ruleFieldOptions}
                      placeholder="Field…"
                    />
                  </div>
                  <div style={{ minWidth: 240 }}>
                    <SearchableSelect
                      value={pendingValue}
                      onChange={setPendingValue}
                      options={ruleValueOptions}
                      placeholder="Search value…"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addRule}
                    disabled={!pendingValue}
                    style={{ padding: "7px 16px", borderRadius: 7, border: "none", background: pendingValue ? "#d97706" : "#e5e7eb", color: pendingValue ? "#fff" : "#9ca3af", fontSize: 13, fontWeight: 700, cursor: pendingValue ? "pointer" : "not-allowed" }}
                  >
                    + Add to List
                  </button>
                  {rules.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setRules([])}
                      style={{ padding: "7px 12px", borderRadius: 7, border: "1px solid #fca5a5", background: "#fff", color: "#dc2626", fontSize: 12, cursor: "pointer" }}
                    >
                      Clear All Rules
                    </button>
                  )}
                </div>

                {/* Rules List */}
                {rules.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                    {rules.map((rule) => {
                      const count = ruleMatchCount(rule);
                      return (
                        <div key={rule.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px 5px 12px", borderRadius: 20, background: "#fff", border: "1px solid #d97706", fontSize: 12, fontWeight: 600, color: "#92400e" }}>
                          <span style={{ fontSize: 10, color: "#b45309", textTransform: "uppercase", marginRight: 2 }}>{FIELD_LABELS[rule.field]}</span>
                          <span>{rule.value}</span>
                          <span style={{ background: "#fef3c7", color: "#b45309", borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>{count}</span>
                          <button
                            type="button"
                            onClick={() => removeRule(rule.id)}
                            style={{ marginLeft: 2, background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 14, lineHeight: 1, padding: "0 2px", fontWeight: 700 }}
                          >×</button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Moving List Summary + Move Action */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderRadius: 10, background: ruleMatchedIds.size > 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${ruleMatchedIds.size > 0 ? "#86efac" : "#e5e7eb"}`, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 22, fontWeight: 800, color: ruleMatchedIds.size > 0 ? "#16a34a" : "#9ca3af" }}>{ruleMatchedIds.size}</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: ruleMatchedIds.size > 0 ? "#166534" : "#6b7280" }}>orders in moving list</div>
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>{rules.length === 0 ? "Add rules above to select orders" : `from ${rules.length} rule(s), de-duplicated`}</div>
                    </div>
                  </div>
                  {ruleMatchedIds.size > 0 && (
                    <>
                      <span style={{ width: 1, height: 32, background: "#d1d5db" }} />
                      <span style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>Move to:</span>
                      <div style={{ minWidth: 260 }}>
                        <SearchableSelect
                          value={moveToPartner}
                          onChange={setMoveToPartner}
                          options={moverTargetOptions}
                          placeholder="Search target partner…"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (!moveToPartner) { setMoveBanner({ kind: "err", text: "Pick a target partner" }); return; }
                          setMoveBanner(null);
                          setMoveConfirmOpen(true);
                        }}
                        disabled={moving || !moveToPartner}
                        style={{ padding: "8px 20px", borderRadius: 7, border: "none", background: moveToPartner ? "#16a34a" : "#d1d5db", color: "#fff", fontSize: 13, fontWeight: 700, cursor: moveToPartner ? "pointer" : "not-allowed" }}
                      >
                        Move {ruleMatchedIds.size} Orders →
                      </button>
                    </>
                  )}
                  {moveBanner && (
                    <span style={{ marginLeft: "auto", padding: "5px 12px", borderRadius: 14, fontSize: 12, fontWeight: 700, background: moveBanner.kind === "ok" ? "#dcfce7" : "#fee2e2", color: moveBanner.kind === "ok" ? "#166534" : "#991b1b" }}>
                      {moveBanner.text}
                    </span>
                  )}
                </div>
              </div>
            )}

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ overflowX: "auto", maxHeight: "580px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ background: "#f0fdf9", position: "sticky", top: 0, zIndex: 2 }}>
                    {selectMode && (
                      <th style={{ padding: "11px 10px", width: 32, textAlign: "center", borderBottom: "2px solid #e5e7eb" }}>
                        <input
                          type="checkbox"
                          checked={filteredShipmentDetails.length > 0 && filteredShipmentDetails.every((r) => selectedOrderIds.has(r.orderId))}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedOrderIds(new Set(filteredShipmentDetails.map((r) => r.orderId).filter((x) => x != null)));
                            else setSelectedOrderIds(new Set());
                          }}
                        />
                      </th>
                    )}
                    {["AWB", "Status", "Order Date", "Partner", "Driver Name", "Shipper", "Consignee", "Pickup", "Ambient", "Chilled", "Frozen", "Total", "Received By"].map((heading) => (
                      <th key={heading} style={{ padding: "11px 10px", textAlign: "left", borderBottom: "2px solid #d1fae5", color: "#0f766e", fontSize: "11px", fontWeight: 800, textTransform: "uppercase", whiteSpace: "nowrap", letterSpacing: "0.5px" }}>{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredShipmentDetails.map((row, index) => {
                    const statusStyle = statusColors[row.status] || { bg: "#f3f4f6", color: "#374151" };
                    const isSelected = selectMode && row.orderId != null && selectedOrderIds.has(row.orderId);
                    return (
                      <tr key={`${row.awb}-${index}`} style={{
                        background: isSelected ? "#eff6ff" : (index % 2 === 0 ? "#fff" : "#fbfdff"),
                        borderBottom: "1px solid #eef2f7",
                      }}>
                        {selectMode && (
                          <td style={{ padding: "10px", textAlign: "center" }}>
                            <input
                              type="checkbox"
                              disabled={row.orderId == null}
                              checked={isSelected}
                              onChange={() => toggleSelectOne(row.orderId)}
                              title={row.orderId == null ? "Row missing orderId — cannot move" : ""}
                            />
                          </td>
                        )}
                        <td style={{ padding: "10px", fontFamily: "monospace", fontWeight: "700", color: "#2563eb", whiteSpace: "nowrap" }}>{row.awb || "--"}</td>
                        <td style={{ padding: "10px" }}><span style={{ padding: "4px 8px", borderRadius: "999px", background: statusStyle.bg, color: statusStyle.color, fontSize: "11px", fontWeight: "800" }}>{row.status || "--"}</span></td>
                        <td style={{ padding: "10px", whiteSpace: "nowrap" }}>{row.orderDate || row.lastTransitDate || "--"}</td>
                        <td style={{ padding: "10px", minWidth: "150px" }}>{row.partnerName}</td>
                        <td style={{ padding: "10px", minWidth: "150px" }}>{row.driverName}</td>
                        <td style={{ padding: "10px", minWidth: "150px" }}>{row.shipper}</td>
                        <td style={{ padding: "10px", minWidth: "210px" }}>{row.consignee}</td>
                        <td style={{ padding: "10px", minWidth: "150px" }}>{row.pickupLocation}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>{row.ambientCount}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>{row.chilledCount}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>{row.frozenCount}</td>
                        <td style={{ padding: "10px", textAlign: "center", fontWeight: "800" }}>{row.totalSamples}</td>
                        <td style={{ padding: "10px", minWidth: "130px" }}>{row.receiverName}</td>
                      </tr>
                    );
                  })}
                  {!filteredShipmentDetails.length && (
                    <tr>
                      <td colSpan="13" style={{ padding: "26px", textAlign: "center", color: "#64748b", fontWeight: "700" }}>No rows match the selected status</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && !sampleLoading && !hasRows && !sampleCollections.length && !error && (
        <div style={{ ...cardStyle, textAlign: "center", color: "#64748b", padding: "56px 48px" }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: "#f1f5f9", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
            <i className="fa fa-file-text-o" style={{ fontSize: "26px", color: "#94a3b8" }} />
          </div>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#374151" }}>No report loaded</div>
          <div style={{ fontSize: "13px", color: "#94a3b8", marginTop: 6 }}>Select your filters above and click Get Report</div>
        </div>
      )}

      {sampleLoading && hasRows && (
        <div style={{ ...cardStyle, marginTop: "18px", color: "#0f766e", fontSize: "13px", fontWeight: "600", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid #d1fae5", borderTopColor: "#0f766e", borderRadius: "50%", animation: "spin 0.7s linear infinite", flexShrink: 0 }} />
          Loading sample collection details…
        </div>
      )}

      {filteredSampleCollections.length > 0 && (
        <div style={{ ...cardStyle, marginTop: "18px", padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid #e5e7eb" }}>
            <h4 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: "#111827" }}>Sample Collection Rows</h4>
          </div>
          <div style={{ overflowX: "auto", maxHeight: "320px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ background: "#f0fdf9", position: "sticky", top: 0, zIndex: 2 }}>
                  {["AWB", "Status", "Order Date", "Partner", "Driver Name", "Shipper", "Ambient", "Chilled", "Frozen", "Total"].map((heading) => (
                    <th key={heading} style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #d1fae5", color: "#0f766e", fontSize: "11px", fontWeight: 800, textTransform: "uppercase", whiteSpace: "nowrap" }}>{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredSampleCollections.map((row, index) => (
                  <tr key={`${row.awb}-sample-${index}`} style={{ borderBottom: "1px solid #eef2f7" }}>
                    <td style={{ padding: "9px", fontFamily: "monospace", color: "#2563eb", fontWeight: "700" }}>{row.awb || "--"}</td>
                    <td style={{ padding: "9px" }}>{row.status || "--"}</td>
                    <td style={{ padding: "9px" }}>{row.orderDate || "--"}</td>
                    <td style={{ padding: "9px" }}>{row.partnerName}</td>
                    <td style={{ padding: "9px" }}>{row.driverName}</td>
                    <td style={{ padding: "9px" }}>{row.shipper}</td>
                    <td style={{ padding: "9px", textAlign: "center" }}>{row.ambientCount}</td>
                    <td style={{ padding: "9px", textAlign: "center" }}>{row.chilledCount}</td>
                    <td style={{ padding: "9px", textAlign: "center" }}>{row.frozenCount}</td>
                    <td style={{ padding: "9px", textAlign: "center", fontWeight: "800" }}>{row.totalSamples}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {moveConfirmOpen && (
        <div
          onClick={() => !moving && setMoveConfirmOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 12, padding: 24, maxWidth: 480, width: "92%",
              boxShadow: "0 24px 48px rgba(15,23,42,0.35)",
            }}
          >
            <h3 style={{ margin: "0 0 12px", color: "#0f172a", fontSize: 18, fontWeight: 800 }}>
              Confirm Partner Move
            </h3>
            <p style={{ margin: "0 0 8px", color: "#334155", fontSize: 14 }}>
              Reassign <strong>{selectedOrderIds.size}</strong> medical order(s) to{" "}
              <strong>{targetPartnerName()}</strong>.
            </p>
            <p style={{ margin: "0 0 18px", color: "#dc2626", fontSize: 13 }}>
              This updates new_shipment_job_manager.partner_id immediately and there is no undo.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setMoveConfirmOpen(false)}
                disabled={moving}
                style={{
                  padding: "8px 16px", borderRadius: 8, border: "1px solid #cbd5e1",
                  background: "#fff", color: "#334155", fontWeight: 600, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={doMoveOrders}
                disabled={moving}
                style={{
                  padding: "8px 16px", borderRadius: 8, border: "none",
                  background: moving ? "#94a3b8" : "#dc2626", color: "#fff",
                  fontWeight: 700, cursor: moving ? "not-allowed" : "pointer",
                }}
              >
                {moving ? "Moving…" : `Yes, move ${selectedOrderIds.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
